import React, { useState, useEffect } from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons, Feather } from "@expo/vector-icons";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import { useDevice } from "../../_layout";
import { shouldSkipUserScopedForms } from "../../invitations/pendingClientDataAccess";

const FormsCard = observer(({ client }) => {
  const { t } = useTranslation();
  const [hoveredForm, setHoveredForm] = useState(null);
  const { isMobile } = useDevice();
  const shouldSkipFormsSync = shouldSkipUserScopedForms(client);

  // Check if the form IDs are configured in coach.forms
  const weeklyFormConfigured = client.weeklyFormId
    ? client.coach.forms.some((f) => f.id === client.weeklyFormId)
    : false;
  const blockFormConfigured = client.blockFormId
    ? client.coach.forms.some((f) => f.id === client.blockFormId)
    : false;

  // Get active block
  const activeBlock = client.summaryStartDate
    ? client.activeBlock(client.summaryStartDate)
    : null;

  // Find forms for the current week
  const weekStart = client.summaryStartDate;
  const weekEnd = weekStart ? new Date(weekStart) : null;
  if (weekEnd) {
    weekEnd.setDate(weekEnd.getDate() + 7);
  }

  const weeklyForm = weekStart
    ? client.forms.find((f) => {
        if (f.type !== "Semanal") return false;
        const formDate = new Date(f.date);
        return formDate >= weekStart && formDate < weekEnd;
      })
    : null;

  // Block form should be the same across all weeks with the same active block
  const blockForm = activeBlock
    ? client.forms.find((f) => {
        if (f.type !== "Fin de bloque") return false;
        return f.blockId === activeBlock.id;
      })
    : null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isFutureWeek = weekStart && weekStart > today;
  const weeklyFormStatusText = isFutureWeek
    ? t("notSent-string")
    : t("notCompleted-string");

  let blockFormStatusText = t("notCompleted-string");
  if (activeBlock && client.summaryStartDate) {
    const totalWeeks = activeBlock.totalWeeks;
    const currentWeek = activeBlock.currentWeek(client.summaryStartDate);
    const weeksUntilLast = totalWeeks - currentWeek;
    const lastWeekStartDate = new Date(client.summaryStartDate);
    lastWeekStartDate.setDate(lastWeekStartDate.getDate() + weeksUntilLast * 7);

    if (today < lastWeekStartDate) {
      blockFormStatusText = t("notSent-string");
    }
  } else if (isFutureWeek) {
    blockFormStatusText = t("notSent-string");
  }

  useEffect(() => {
    const fetchMissingForms = async () => {
      if (shouldSkipFormsSync) return;
      if (!client.summaryStartDate || !weeklyFormConfigured) return;

      const weekStart = client.summaryStartDate;
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);

      // Only fetch weekly forms for past weeks (not current, not future)
      if (weekStart < today && weekEnd <= today) {
        await client.fetchFormsFromCloudBetweenDates(weekStart, weekEnd, [
          "Semanal",
        ]);
      }
    };

    fetchMissingForms();
  }, [client.summaryStartDate, weeklyFormConfigured, shouldSkipFormsSync]);

  // Fetch block form when active block changes
  useEffect(() => {
    const fetchBlockForm = async () => {
      if (shouldSkipFormsSync) return;
      if (!activeBlock || !blockFormConfigured) return;
      await client.fetchFormByBlockId(activeBlock.id);
    };

    fetchBlockForm();
  }, [activeBlock?.id, blockFormConfigured, shouldSkipFormsSync]);

  // Don't show card if no forms are configured
  if (!weeklyFormConfigured && !blockFormConfigured) {
    return (
      <View style={[GlobalStyles.Card, styles.halfCard]}>
        <View style={GlobalStyles.CardHeader}>
          <View style={GlobalStyles.CardHeaderMain}>
            <View style={[GlobalStyles.CardIcon, GlobalStyles.SummaryCardIcon]}>
              <Ionicons
                name="reader-outline"
                size={16}
                color={Colors.grayColor}
              />
            </View>
            <Text style={GlobalStyles.CardTitle}>{t("formsTitle-string")}</Text>
          </View>
        </View>
        <View
          style={{
            justifyContent: "center",
            flex: 1,
          }}
        >
          <View style={styles.formsList}>
            {[1, 2].map((_, index) => (
              <View
                key={index}
                style={[
                  styles.formRow,
                  index === 1 && styles.formRowLast,
                  { opacity: 0.5 },
                ]}
              >
                <View
                  style={{
                    width: 100,
                    height: 14,
                    backgroundColor: Colors.lightGrayColor,
                    borderRadius: 4,
                  }}
                />
                <View
                  style={{
                    width: 80,
                    height: 12,
                    backgroundColor: Colors.lightGrayColor,
                    borderRadius: 4,
                  }}
                />
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[GlobalStyles.Card, styles.halfCard]}>
      <View style={GlobalStyles.CardHeader}>
        <View style={GlobalStyles.CardHeaderMain}>
          <View style={[GlobalStyles.CardIcon, GlobalStyles.SummaryCardIcon]}>
            <Ionicons
              name="reader-outline"
              size={16}
              color={Colors.grayColor}
            />
          </View>
          <Text style={GlobalStyles.CardTitle}>{t("formsTitle-string")}</Text>
        </View>
      </View>

      <View style={[styles.formsList, { flex: 1, justifyContent: "center" }]}>
        {blockFormConfigured && (
          <TouchableOpacity
            style={[
              styles.formRow,
              !weeklyFormConfigured && styles.formRowLast,
            ]}
            onMouseEnter={() => !isMobile && blockForm && setHoveredForm(0)}
            onMouseLeave={() => setHoveredForm(null)}
            onPress={() => {
              if (blockForm) {
                client.setSelectedForm(blockForm);
                client.setProperty("selectedTab", "forms-string");
              }
            }}
            disabled={!blockForm}
          >
            <Text style={styles.formRowTitle} numberOfLines={3}>
              {t("blockForm-string")}
            </Text>
            <View style={styles.formRightSection}>
              <Feather
                name="arrow-up-right"
                size={16}
                color={Colors.grayColor}
                style={{
                  marginRight: 8,
                  opacity: hoveredForm === 0 || (isMobile && blockForm) ? 1 : 0,
                }}
              />
              {blockForm ? (
                <>
                  {blockForm.numberOfQuestionsAnswered > 0 &&
                  !blockForm.getSeenByCoach ? (
                    <View style={styles.formStatusBadgeReview}>
                      <Text style={styles.formStatusReviewText}>
                        {t("review-string")}
                      </Text>
                      <Ionicons name="eye" size={14} color="#92400E" />
                    </View>
                  ) : (
                    <View style={styles.formStatusContainer}>
                      <Text
                        style={styles.formStatusCompleted}
                        numberOfLines={2}
                      >
                        {t("completed-string")} (
                        {blockForm.numberOfQuestionsAnswered}/
                        {blockForm.numberOfQuestionsAsked})
                      </Text>
                    </View>
                  )}
                </>
              ) : (
                <View style={styles.formStatusContainer}>
                  <Text style={styles.formStatusNotCompleted}>
                    {blockFormStatusText}
                  </Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        )}

        {weeklyFormConfigured && (
          <TouchableOpacity
            style={[styles.formRow, styles.formRowLast]}
            onMouseEnter={() => !isMobile && weeklyForm && setHoveredForm(1)}
            onMouseLeave={() => setHoveredForm(null)}
            onPress={() => {
              if (weeklyForm) {
                client.setSelectedForm(weeklyForm);
                client.setProperty("selectedTab", "forms-string");
              }
            }}
            disabled={!weeklyForm}
          >
            <Text style={styles.formRowTitle} numberOfLines={3}>
              {t("weeklyForm-string")}
            </Text>
            <View style={styles.formRightSection}>
              <Feather
                name="arrow-up-right"
                size={16}
                color={Colors.grayColor}
                style={{
                  marginRight: 8,
                  opacity:
                    hoveredForm === 1 || (isMobile && weeklyForm) ? 1 : 0,
                }}
              />
              {weeklyForm ? (
                <>
                  {weeklyForm.numberOfQuestionsAnswered > 0 &&
                  !weeklyForm.getSeenByCoach ? (
                    <View style={styles.formStatusBadgeReview}>
                      <Text style={styles.formStatusReviewText}>
                        {t("review-string")}
                      </Text>
                      <Ionicons name="eye" size={14} color="#92400E" />
                    </View>
                  ) : (
                    <View style={styles.formStatusContainer}>
                      <Text
                        style={styles.formStatusCompleted}
                        numberOfLines={2}
                      >
                        {t("completed-string")} (
                        {weeklyForm.numberOfQuestionsAnswered}/
                        {weeklyForm.numberOfQuestionsAsked})
                      </Text>
                    </View>
                  )}
                </>
              ) : (
                <View style={styles.formStatusContainer}>
                  <Text style={styles.formStatusNotCompleted}>
                    {weeklyFormStatusText}
                  </Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        )}
      </View>
      {client.isOffline && (weeklyFormConfigured || blockFormConfigured) && (
        <View style={styles.offlineWarningContainer}>
          <Ionicons
            name="information-circle-outline"
            size={14}
            color={Colors.grayColor}
          />
          <Text style={styles.offlineWarningText}>
            {t("offlineFormsInformationMissing-string")}
          </Text>
        </View>
      )}
      {shouldSkipFormsSync && (
        <View style={styles.offlineWarningContainer}>
          <Ionicons
            name="information-circle-outline"
            size={14}
            color={Colors.grayColor}
          />
          <Text style={styles.offlineWarningText}>
            {t("pendingClientFormsAfterAcceptance-string")}
          </Text>
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  halfCard: {
    flex: 1,
    minWidth: 240,
  },
  formsList: {
    gap: 0,
  },
  formRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderColor,
  },
  formRowLast: {
    borderBottomWidth: 0,
  },
  formRowTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.blackColor,
  },
  formRightSection: {
    flexDirection: "row",
    alignItems: "center",
  },
  formStatusContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    flexShrink: 1,
  },
  formStatusCompleted: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.bubbleColor,
    flexShrink: 1,
  },
  formStatusBadgeReview: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "#FEF3C7",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FCD34D",
  },
  formStatusReviewText: {
    fontSize: 12,
    fontWeight: "700",
    color: "#92400E",
  },
  formStatusNotCompleted: {
    fontSize: 12,
    fontWeight: "600",
    color: Colors.grayColor,
  },
  offlineWarningContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingTop: 12,
    gap: 4,
    opacity: 0.8,
  },
  offlineWarningText: {
    fontSize: 11,
    color: Colors.grayColor,
  },
});

export default FormsCard;
